import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Upload } from "lucide-react";
import { useFileUpload } from "@/lib/uploadProgress";

const empty = {
  name: "",
  item_count: 4,
  price: "",
  all_products: false,
  product_ids: [],
  banner_desktop: "",
  banner_mobile: "",
  headline: "Build Your Own Box",
  offer_text: "",
  is_active: true,
};

function BannerField({ label, value, onChange, testId }) {
  const uploadFile = useFileUpload();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "image");
    setBusy(true);
    try {
      const data = await uploadFile("/merchant/media/upload", fd);
      onChange(data.url);
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-2 flex items-center gap-3">
        {value ? <img src={mediaUrl(value)} alt="" className="h-16 w-28 object-cover rounded-lg border" /> : <div className="h-16 w-28 rounded-lg border border-dashed border-slate-300 bg-slate-50" />}
        <label className="inline-flex items-center gap-2 text-sm text-blue-700 cursor-pointer" data-testid={testId}>
          <Upload className="w-4 h-4" /> {busy ? "Uploading…" : (value ? "Replace" : "Upload")}
          <input type="file" hidden accept="image/*" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        </label>
      </div>
    </div>
  );
}

export default function AdminBuildBoxForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = Boolean(id);
  const [form, setForm] = useState(empty);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/merchant/products").then(({ data }) => setProducts(Array.isArray(data) ? data : [])).catch(() => {});
    if (!id) return;
    api.get(`/merchant/build-boxes/${id}`).then(({ data }) => {
      setForm({
        name: data.name || "",
        item_count: data.item_count || 4,
        price: data.price ?? "",
        all_products: Boolean(data.all_products),
        product_ids: data.product_ids || [],
        banner_desktop: data.banner_desktop || "",
        banner_mobile: data.banner_mobile || "",
        headline: data.headline || "Build Your Own Box",
        offer_text: data.offer_text || "",
        is_active: data.is_active !== false,
      });
    }).catch((e) => { toast.error(extractErr(e)); nav("/admin/build-boxes"); });
  }, [id, nav]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return products;
    return products.filter((p) => String(p.name || "").toLowerCase().includes(needle));
  }, [products, q]);

  const toggle = (pid) => {
    setForm((f) => ({
      ...f,
      product_ids: f.product_ids.includes(pid) ? f.product_ids.filter((x) => x !== pid) : [...f.product_ids, pid],
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {
      ...form,
      item_count: Number(form.item_count),
      price: Number(form.price),
    };
    try {
      if (editing) await api.put(`/merchant/build-boxes/${id}`, body);
      else await api.post("/merchant/build-boxes", body);
      toast.success(editing ? "Box updated" : "Box created");
      nav("/admin/build-boxes");
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-3xl space-y-5" data-testid="build-box-form">
      <Link to="/admin/build-boxes" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Boxes</Link>
      <h1 className="text-2xl font-bold text-slate-900">{editing ? "Edit box" : "New box"}</h1>
      <Card className="p-5 space-y-4">
        <div>
          <Label>Box name</Label>
          <Input required className="mt-1" data-testid="build-box-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Build Your Own Box" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Number of products in the box</Label>
            <Input required type="number" min={2} max={24} className="mt-1" data-testid="build-box-count" value={form.item_count} onChange={(e) => setForm({ ...form, item_count: e.target.value })} />
          </div>
          <div>
            <Label>Box price (₹)</Label>
            <Input required type="number" min={0} step="0.01" className="mt-1" data-testid="build-box-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Banner headline</Label>
            <Input className="mt-1" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} />
          </div>
          <div>
            <Label>Offer line</Label>
            <Input className="mt-1" value={form.offer_text} onChange={(e) => setForm({ ...form, offer_text: e.target.value })} placeholder="BUY ANY 4 AT" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
          Box is live
        </label>
      </Card>
      <Card className="p-5 space-y-4" data-testid="build-box-banners">
        <div className="font-semibold text-slate-900">Banners</div>
        <p className="text-sm text-slate-500">Desktop banner shows on larger screens. Mobile banner shows on phones.</p>
        <BannerField label="Desktop banner" value={form.banner_desktop} onChange={(url) => setForm({ ...form, banner_desktop: url })} testId="build-box-banner-desktop" />
        <BannerField label="Mobile banner" value={form.banner_mobile} onChange={(url) => setForm({ ...form, banner_mobile: url })} testId="build-box-banner-mobile" />
      </Card>
      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="font-semibold text-slate-900">Eligible products</div>
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" data-testid="build-box-all" checked={form.all_products} onChange={(e) => setForm({ ...form, all_products: e.target.checked })} />
            All products
          </label>
        </div>
        {!form.all_products ? (
          <>
            <div className="flex gap-2">
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" data-testid="build-box-product-search" />
              <Button type="button" variant="outline" onClick={() => setForm({ ...form, product_ids: products.map((p) => p.id) })}>Select all</Button>
              <Button type="button" variant="outline" onClick={() => setForm({ ...form, product_ids: [] })}>Clear</Button>
            </div>
            <div className="max-h-80 overflow-auto divide-y border rounded-xl" data-testid="build-box-products">
              {shown.map((p) => (
                <label key={p.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={() => toggle(p.id)} />
                  <span className="flex-1 truncate">{p.name}</span>
                  <span className="text-slate-500">₹{Number(p.price || 0).toLocaleString("en-IN")}</span>
                </label>
              ))}
              {!shown.length ? <p className="p-3 text-sm text-slate-500">No products found.</p> : null}
            </div>
            <p className="text-xs text-slate-500">{form.product_ids.length} selected</p>
          </>
        ) : <p className="text-sm text-slate-500">Shoppers can pick any active product in the store.</p>}
      </Card>
      <Button type="submit" disabled={saving} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="build-box-save">{saving ? "Saving…" : "Save box"}</Button>
    </form>
  );
}
